import type {StepDefinition} from '~/models/wizardSchema'
import type {FinishSectionConfig} from '~/components/Wizard/Sections/Finish/FinishSection.settings'
import {
    CONTRACT_TYPE_FOREST_OPERATION,
    CONTRACT_TYPE_REGISTRATION,
    CONTRACT_TYPE_SILVICULTURE_AGREEMENT,
    CONTRACT_TYPE_SILVICULTURE_ORDER,
    CONTRACT_TYPE_SUPPLY_WOOD,
    ENTITY_TYPE_QUOTE,
} from '~/constants/contracts'
import {SECTION_ID, WIZARD_PAGE} from '~/models/wizardIds'
import {noSilviculturePlans} from '~/helpers/stepHideRules'

const forestOperationSteps: StepDefinition[] = [
    {
        Page: WIZARD_PAGE.LOGGING_PERIOD,
        Title: 'WIZARD_STEPS.LOGGING_PERIOD',
        sections: [
            {sectionId: SECTION_ID.LOGGING_PERIOD},
        ],
    },
    {
        Page: WIZARD_PAGE.ACTION_PLANS,
        Title: 'WIZARD_STEPS.ACTIONPLANS_TITLE',
        sections: [
            {sectionId: SECTION_ID.ACTION_PLANS},
        ],
    },
    {
        Page: WIZARD_PAGE.SILVICULTURE_PRICES,
        Title: 'WIZARD_STEPS.SILVICULTURE_ACTION_PLANS',
        hiddenWhen: [noSilviculturePlans],
        sections: [
            {sectionId: SECTION_ID.YOUNG_FOREST_CARE_QUESTIONS},
            {sectionId: SECTION_ID.SILVICULTURE_PRICES},
            {sectionId: SECTION_ID.SILVICULTURE_NOTES},
        ],
    },
    {
        Page: WIZARD_PAGE.ENVIRONMENT_REPORT,
        Title: 'WIZARD_STEPS.ENVIRONMENT_REPORT',
        sections: [
            {sectionId: SECTION_ID.ENVIRONMENT_REPORT_V2},
        ],
    },
    {
        Page: WIZARD_PAGE.ADDITIONS,
        Title: 'WIZARD_STEPS.ADDITIONS',
        sections: [
            {sectionId: SECTION_ID.ADDITIONS},
        ],
    },
    {
        Page: WIZARD_PAGE.VOLUME_AND_ENTREPRENEUR,
        Title: 'WIZARD_STEPS.VOLUME_ENTREPRENEUR',
        sections: [
            {sectionId: SECTION_ID.VOLUME},
            {sectionId: SECTION_ID.ENTREPRENEUR},
            {sectionId: SECTION_ID.ENTREPRENEUR_CAPACITY},
            {sectionId: SECTION_ID.OPERATION, config: {required: false}},
        ],
    },
    {
        Page: WIZARD_PAGE.ASSORTMENTS,
        Title: 'WIZARD_STEPS.ASSORTMENT_AND_PRICES',
        sections: [
            {sectionId: SECTION_ID.ASSORTMENTS},
        ],
    },
    {
        Page: WIZARD_PAGE.TOPPLE_PLACES,
        Title: 'WIZARD_STEPS.COLLECTION_PLACE',
        sections: [
            {sectionId: SECTION_ID.TOPPLE_PLACES},
        ],
    },
    {
        Page: WIZARD_PAGE.AGREEMENT,
        Title: 'WIZARD_STEPS.FOREST_REMOVAL_MISSION',
        sections: [
            {sectionId: SECTION_ID.OPERATION_PRICE},
            {sectionId: SECTION_ID.SCOPE},
            {sectionId: SECTION_ID.GOALS_FOR_OPERATION_FINISHED},
            {sectionId: SECTION_ID.ROAD_CONTACT},
        ],
    },
    {
        Page: WIZARD_PAGE.FINAL_DETAILS,
        Title: 'WIZARD_STEPS.FINAL_DETAILS',
        sections: [
            {sectionId: SECTION_ID.FOREST_FUND},
            {sectionId: SECTION_ID.COLLECTION_POINT},
            {sectionId: SECTION_ID.ORDERS},
            {sectionId: SECTION_ID.FINAL_DETAILS_NOTES},
            {sectionId: SECTION_ID.DOCUMENTS},
            {sectionId: SECTION_ID.FINAL_DETAILS_ELIGIBILITY},
        ],
    },
    {
        Page: WIZARD_PAGE.SUMMARY,
        Title: 'WIZARD_STEPS.SUMMARY',
        sections: [
            {sectionId: SECTION_ID.SUMMARY_DETAILS},
            {sectionId: SECTION_ID.SUMMARY_PARTIES},
            {sectionId: SECTION_ID.SUMMARY_PROPERTY},
            {sectionId: SECTION_ID.SUMMARY_OPERATING_AGREEMENT},
            {sectionId: SECTION_ID.SUMMARY_SILVICULTURE},
            {sectionId: SECTION_ID.SUMMARY_ADDITIONAL},
            {sectionId: SECTION_ID.SUMMARY_MAP_IMAGES},
        ],
    },
    {
        Page: WIZARD_PAGE.COMBINED_SIGN_AND_FINISH,
        Title: 'WIZARD_STEPS.SIGNATURE',
        sections: [
            {sectionId: SECTION_ID.COMBINED_SIGN_AND_FINISH},
        ],
    },
]

const silvicultureOrderSteps: StepDefinition[] = [
    {
        Page: WIZARD_PAGE.ACTION_PLANS,
        Title: 'WIZARD_STEPS.ACTIONPLANS_TITLE',
        sections: [
            {sectionId: SECTION_ID.ACTION_PLANS},
        ],
    },
    {
        Page: WIZARD_PAGE.SILVICULTURE,
        Title: 'WIZARD_STEPS.SILVICULTURE_DETAILS',
        sections: [
            {sectionId: SECTION_ID.STAND_INFORMATION},
            {sectionId: SECTION_ID.SILVICULTURE_NOTES, config: {showSilvicultureAgreementNote: false}},
        ],
    },
    {
        Page: WIZARD_PAGE.FINAL_DETAILS,
        Title: 'WIZARD_STEPS.FINAL_DETAILS',
        sections: [
            {sectionId: SECTION_ID.ORDERS},
            {sectionId: SECTION_ID.FINAL_DETAILS_ELIGIBILITY},
        ],
    },
    {
        Page: WIZARD_PAGE.DETAILS,
        Title: 'WIZARD_STEPS.DETAILS',
        sections: [
            {sectionId: SECTION_ID.DETAILS_STOCK_LEVEL},
            {sectionId: SECTION_ID.FINISH_ORDER},
        ],
    },
]

const silvicultureAgreementSteps: StepDefinition[] = [
    {
        Page: WIZARD_PAGE.LOGGING_PERIOD,
        Title: 'WIZARD_STEPS.PERIOD',
        sections: [
            {sectionId: SECTION_ID.LOGGING_PERIOD, config: {isSilvicultureAgreement: true}},
        ],
    },
    {
        Page: WIZARD_PAGE.ACTION_PLANS,
        Title: 'WIZARD_STEPS.ACTIONPLANS_TITLE',
        sections: [
            {sectionId: SECTION_ID.ACTION_PLANS},
        ],
    },
    {
        Page: WIZARD_PAGE.LOCATION,
        Title: 'WIZARD_STEPS.LOCATION_NAME',
        sections: [
            {sectionId: SECTION_ID.LOCATION},
        ],
    },
    {
        Page: WIZARD_PAGE.ENVIRONMENT_REPORT,
        Title: 'WIZARD_STEPS.ENVIRONMENT_REPORT',
        sections: [
            {sectionId: SECTION_ID.ENVIRONMENT_REPORT_V2},
        ],
    },
    {
        Page: WIZARD_PAGE.DETAILS,
        Title: 'WIZARD_STEPS.DETAILS',
        sections: [
            {sectionId: SECTION_ID.DETAILS_STOCK_LEVEL},
        ],
    },
    {
        Page: WIZARD_PAGE.SILVICULTURE_SUMMARY,
        Title: 'WIZARD_STEPS.SUMMARY',
        sections: [
            {sectionId: SECTION_ID.LEGACY_SUMMARY},
            {sectionId: SECTION_ID.SILVICULTURE_ACTIONS_SUMMARY},
            {sectionId: SECTION_ID.SILVICULTURE_SUMMARY_NOTES},
            {sectionId: SECTION_ID.SILVICULTURE_SUMMARY_MAP_IMAGES},
        ],
    },
    {
        Page: WIZARD_PAGE.COMBINED_SIGN_AND_FINISH,
        Title: 'WIZARD_STEPS.SIGNATURE',
        sections: [
            {sectionId: SECTION_ID.COMBINED_SIGN_AND_FINISH},
        ],
    },
]

const registrationSteps: StepDefinition[] = [
    {
        Page: WIZARD_PAGE.LOGGING_PERIOD,
        Title: 'WIZARD_STEPS.LOGGING_PERIOD',
        sections: [
            {sectionId: SECTION_ID.INTERNAL_NO},
            {sectionId: SECTION_ID.LOGGING_PERIOD},
        ],
    },
    {
        Page: WIZARD_PAGE.ACTION_PLANS,
        Title: 'WIZARD_STEPS.ACTIONPLANS_TITLE',
        sections: [
            {sectionId: SECTION_ID.ACTION_PLANS},
        ],
    },
    {
        Page: WIZARD_PAGE.VOLUME_AND_ENTREPRENEUR,
        Title: 'WIZARD_STEPS.VOLUME_ENTREPRENEUR',
        sections: [
            {sectionId: SECTION_ID.VOLUME, config: {showCorrection: false, showWoodForOwnUse: false}},
            {sectionId: SECTION_ID.ENTREPRENEUR},
        ],
    },
    {
        Page: WIZARD_PAGE.ASSORTMENTS,
        Title: 'WIZARD_STEPS.ASSORTMENT_AND_PRICES',
        sections: [
            {sectionId: SECTION_ID.ASSORTMENTS},
        ],
    },
    {
        Page: WIZARD_PAGE.TOPPLE_PLACES,
        Title: 'WIZARD_STEPS.COLLECTION_PLACE',
        sections: [
            {sectionId: SECTION_ID.TOPPLE_PLACES},
        ],
    },
    {
        Page: WIZARD_PAGE.AGREEMENT,
        Title: 'WIZARD_STEPS.FOREST_REMOVAL_MISSION',
        sections: [
            {sectionId: SECTION_ID.ROAD_CONTACT},
        ],
    },
    {
        Page: WIZARD_PAGE.FINAL_DETAILS,
        Title: 'WIZARD_STEPS.FINAL_DETAILS',
        sections: [
            {sectionId: SECTION_ID.FOREST_FUND},
            {sectionId: SECTION_ID.COLLECTION_POINT},
            {sectionId: SECTION_ID.ORDERS},
            {sectionId: SECTION_ID.FINAL_DETAILS_NOTES},
            {sectionId: SECTION_ID.DOCUMENTS},
            {sectionId: SECTION_ID.FINAL_DETAILS_ELIGIBILITY},
        ],
    },
    {
        Page: WIZARD_PAGE.DETAILS,
        Title: 'WIZARD_STEPS.DETAILS',
        sections: [
            {sectionId: SECTION_ID.REGISTRATION_DETAILS},
            {sectionId: SECTION_ID.FINISH_ORDER},
        ],
    },
]

const supplyWoodSteps: StepDefinition[] = [
    {
        Page: WIZARD_PAGE.LOGGING_PERIOD,
        Title: 'WIZARD_STEPS.LOGGING_PERIOD',
        sections: [
            {sectionId: SECTION_ID.INTERNAL_NO},
            {sectionId: SECTION_ID.LOGGING_PERIOD},
        ],
    },
    {
        Page: WIZARD_PAGE.ACTION_PLANS,
        Title: 'WIZARD_STEPS.ACTIONPLANS_TITLE',
        sections: [
            {sectionId: SECTION_ID.ACTION_PLANS},
        ],
    },
    {
        Page: WIZARD_PAGE.SILVICULTURE,
        Title: 'WIZARD_STEPS.SILVICULTURE_DETAILS',
        sections: [
            {sectionId: SECTION_ID.YOUNG_FOREST_CARE_QUESTIONS},
            {sectionId: SECTION_ID.SILVICULTURE_NOTES},
        ],
    },
    {
        Page: WIZARD_PAGE.ENVIRONMENT_REPORT,
        Title: 'WIZARD_STEPS.DUE_DILIGENCE_ASSESSMENT',
        sections: [
            {sectionId: SECTION_ID.ENVIRONMENT_REPORT_V2, config: { showAfterOperationTab: true, showEnvironmentResponsible: true }},
        ],
    },
    {
        Page: WIZARD_PAGE.VOLUME_AND_ENTREPRENEUR,
        Title: 'WIZARD_STEPS.VOLUME_ENTREPRENEUR',
        sections: [
            {sectionId: SECTION_ID.VOLUME, config: {showWoodForOwnUse: false, largeForestOwnerExempt: true}},
            {sectionId: SECTION_ID.ENTREPRENEUR},
        ],
    },
    {
        Page: WIZARD_PAGE.ASSORTMENTS,
        Title: 'WIZARD_STEPS.ASSORTMENT_AND_PRICES',
        sections: [
            {sectionId: SECTION_ID.ASSORTMENTS},
        ],
    },
    {
        Page: WIZARD_PAGE.TOPPLE_PLACES,
        Title: 'WIZARD_STEPS.COLLECTION_PLACE',
        sections: [
            {sectionId: SECTION_ID.TOPPLE_PLACES},
        ],
    },
    {
        Page: WIZARD_PAGE.AGREEMENT,
        Title: 'WIZARD_STEPS.FOREST_REMOVAL_MISSION',
        sections: [
            {sectionId: SECTION_ID.ROAD_CONTACT},
        ],
    },
    {
        Page: WIZARD_PAGE.FINAL_DETAILS,
        Title: 'WIZARD_STEPS.FINAL_DETAILS',
        sections: [
            {sectionId: SECTION_ID.FOREST_FUND},
            {sectionId: SECTION_ID.COLLECTION_POINT},
            {sectionId: SECTION_ID.ORDERS},
            {sectionId: SECTION_ID.FINAL_DETAILS_NOTES},
            {sectionId: SECTION_ID.DOCUMENTS},
            {sectionId: SECTION_ID.FINAL_DETAILS_ELIGIBILITY},
        ],
    },
    {
        Page: WIZARD_PAGE.SUMMARY,
        Title: 'WIZARD_STEPS.SUMMARY',
        sections: [
            {sectionId: SECTION_ID.SUMMARY_DETAILS},
            {sectionId: SECTION_ID.SUMMARY_PARTIES},
            {sectionId: SECTION_ID.SUMMARY_PROPERTY},
            {sectionId: SECTION_ID.SUMMARY_SUPPLY_WOOD_AGREEMENT},
            {sectionId: SECTION_ID.SUMMARY_DUE_DILIGENCE},
            {sectionId: SECTION_ID.SUMMARY_SILVICULTURE},
            {sectionId: SECTION_ID.SUMMARY_ADDITIONAL},
            {sectionId: SECTION_ID.SUMMARY_MAP_IMAGES},
        ],
    },
    {
        Page: WIZARD_PAGE.COMBINED_SIGN_AND_FINISH,
        Title: 'WIZARD_STEPS.SIGNATURE',
        sections: [
            {sectionId: SECTION_ID.COMBINED_SIGN_AND_FINISH, config: {allowFrameworkAgreementSkip: false}},
        ],
    },
]

export const CONTRACT_TYPE_STEP_DEFINITIONS: Record<number, StepDefinition[]> = {
    [CONTRACT_TYPE_FOREST_OPERATION]: forestOperationSteps,
    [CONTRACT_TYPE_SILVICULTURE_ORDER]: silvicultureOrderSteps,
    [CONTRACT_TYPE_SILVICULTURE_AGREEMENT]: silvicultureAgreementSteps,
    [CONTRACT_TYPE_REGISTRATION]: registrationSteps,
    [CONTRACT_TYPE_SUPPLY_WOOD]: supplyWoodSteps,
}

const quoteFinishConfig: FinishSectionConfig = {
    sendButtonLabel: 'QUOTE.SEND',
    recipients: {
        headingKey: 'QUOTE.SEND_TO_HEADING',
        missingEmailKey: 'QUOTE.MISSING_EMAIL_DESC',
    },
}

const quoteForestOperationSteps: StepDefinition[] = [
    {
        Page: WIZARD_PAGE.QUOTE_DETAILS,
        Title: 'WIZARD_STEPS.QUOTE_DETAILS',
        sections: [
            {
                sections: [
                    {sectionId: SECTION_ID.QUOTE_ACCEPT_DEADLINE},
                    {sectionId: SECTION_ID.QUOTE_NAME},
                    {sectionId: SECTION_ID.FOREST_FUND},
                ],
            },
            {
                title: 'WIZARD_STEPS.PERIOD_AND_OPERATION',
                sections: [
                    {sectionId: SECTION_ID.LOGGING_PERIOD},
                    {sectionId: SECTION_ID.OPERATION, config: {required: false}},
                ],
            },
            {sectionId: SECTION_ID.QUOTE_NOTES},
        ],
    },
    {
        Page: WIZARD_PAGE.ACTION_PLANS,
        Title: 'WIZARD_STEPS.ACTIONPLANS_TITLE',
        sections: [
            {sectionId: SECTION_ID.ACTION_PLANS, config: {required: false}},
        ],
    },
    {
        Page: WIZARD_PAGE.SILVICULTURE_PRICES,
        Title: 'WIZARD_STEPS.SILVICULTURE_ACTION_PLANS',
        hiddenWhen: [noSilviculturePlans],
        sections: [
            {
                sectionId: SECTION_ID.SILVICULTURE_NOTES, config: {
                    showWorkOrderNotes: false,
                    silvicultureAgreementNoteLabelKey: 'NOTE_TO_SILVICULTURE_QUOTE',
                    silvicultureAgreementNoteSubLabelKey: 'CONTACT_NOTE_DESCRIPTION_QUOTE',
                }
            },
            {
                title: 'WIZARD_STEPS.ASSIGNMENTS',
                sections: [{sectionId: SECTION_ID.SILVICULTURE_PRICES}]
            },
        ],
    },
    {
        Page: WIZARD_PAGE.ASSORTMENTS,
        Title: 'WIZARD_STEPS.VOLUME_AND_ASSORTMENTS',
        sections: [
            {
                sectionId: SECTION_ID.ASSORTMENTS, config: {
                    showPriceType: false,
                    showTotalWoodForSale: true,
                    limitTo100ByDefault: false,
                    priceMode: 'loggingTimeOrFixedPrice',
                }
            },
        ],
    },
    {
        Page: WIZARD_PAGE.AGREEMENT,
        Title: 'WIZARD_STEPS.OPERATION_PRICE',
        sections: [
            {sectionId: SECTION_ID.OPERATION_PRICE},
        ],
    },
    {
        Page: WIZARD_PAGE.SUMMARY,
        Title: 'WIZARD_STEPS.SUMMARY',
        sections: [
            {sectionId: SECTION_ID.SUMMARY_PDF_PREVIEW},
            {sectionId: SECTION_ID.SUMMARY_CERTIFICATION_WARNING},
            {sectionId: SECTION_ID.SUMMARY_QUOTE_DETAILS},
            {sectionId: SECTION_ID.SUMMARY_PARTIES, config: {showBankAccount: false}},
            {sectionId: SECTION_ID.SUMMARY_PROPERTY},
            {
                sectionId: SECTION_ID.SUMMARY_OPERATING_AGREEMENT,
                config: {
                    showDivisionOfResponsibilities: false,
                    rows: ['loggingPeriod', 'operationPrice', 'operationPriceNote', 'operationProperty', 'note', 'commonPriceNote'],
                },
            },
            {sectionId: SECTION_ID.SUMMARY_SILVICULTURE, config: {showWorkOrderNotes: false}},
            {sectionId: SECTION_ID.SUMMARY_MAP_IMAGES},
        ],
    },
    {
        Page: WIZARD_PAGE.FINISH_QUOTE,
        Title: 'WIZARD_STEPS.FINISH',
        sections: [
            {sectionId: SECTION_ID.FINISH, config: quoteFinishConfig},
        ],
    },
]

const quoteSupplyWoodSteps: StepDefinition[] = [
    {
        Page: WIZARD_PAGE.QUOTE_DETAILS,
        Title: 'WIZARD_STEPS.QUOTE_DETAILS',
        sections: [
            {
                sections: [
                    {sectionId: SECTION_ID.QUOTE_ACCEPT_DEADLINE},
                    {sectionId: SECTION_ID.QUOTE_NAME},
                    {sectionId: SECTION_ID.FOREST_FUND},
                ],
            },
            {
                title: 'WIZARD_STEPS.PERIOD_AND_OPERATION',
                sections: [
                    {sectionId: SECTION_ID.LOGGING_PERIOD},
                    {sectionId: SECTION_ID.OPERATION, config: {required: false}},
                ],
            },
            {sectionId: SECTION_ID.QUOTE_NOTES},
        ],
    },
    {
        Page: WIZARD_PAGE.ACTION_PLANS,
        Title: 'WIZARD_STEPS.ACTIONPLANS_TITLE',
        sections: [
            {sectionId: SECTION_ID.ACTION_PLANS, config: {required: false}},
        ],
    },
    {
        Page: WIZARD_PAGE.SILVICULTURE_PRICES,
        Title: 'WIZARD_STEPS.SILVICULTURE_ACTION_PLANS',
        hiddenWhen: [noSilviculturePlans],
        sections: [
            {
                sectionId: SECTION_ID.SILVICULTURE_NOTES, config: {
                    showWorkOrderNotes: false,
                    silvicultureAgreementNoteLabelKey: 'NOTE_TO_SILVICULTURE_QUOTE',
                    silvicultureAgreementNoteSubLabelKey: 'CONTACT_NOTE_DESCRIPTION_QUOTE',
                }
            },
            {
                title: 'WIZARD_STEPS.ASSIGNMENTS',
                sections: [{sectionId: SECTION_ID.SILVICULTURE_PRICES}]
            },
        ],
    },
    {
        Page: WIZARD_PAGE.ASSORTMENTS,
        Title: 'WIZARD_STEPS.VOLUME_AND_ASSORTMENTS',
        sections: [
            {
                sectionId: SECTION_ID.ASSORTMENTS, config: {
                    showPriceType: false,
                    showTotalWoodForSale: true,
                    limitTo100ByDefault: false,
                    priceMode: 'loggingTimeOrFixedPrice',
                }
            },
        ],
    },
    {
        Page: WIZARD_PAGE.SUMMARY,
        Title: 'WIZARD_STEPS.SUMMARY',
        sections: [
            {sectionId: SECTION_ID.SUMMARY_PDF_PREVIEW},
            {sectionId: SECTION_ID.SUMMARY_CERTIFICATION_WARNING},
            {sectionId: SECTION_ID.SUMMARY_QUOTE_DETAILS},
            {sectionId: SECTION_ID.SUMMARY_PARTIES, config: {showBankAccount: false}},
            {sectionId: SECTION_ID.SUMMARY_PROPERTY},
            {sectionId: SECTION_ID.SUMMARY_SUPPLY_WOOD_AGREEMENT, config: {rows: ['loggingPeriod', 'note', 'commonPriceNote']}},
            {sectionId: SECTION_ID.SUMMARY_SILVICULTURE, config: {showWorkOrderNotes: false}},
            {sectionId: SECTION_ID.SUMMARY_MAP_IMAGES},
        ],
    },
    {
        Page: WIZARD_PAGE.FINISH_QUOTE,
        Title: 'WIZARD_STEPS.FINISH',
        sections: [
            {sectionId: SECTION_ID.FINISH, config: quoteFinishConfig},
        ],
    },
]

export const QUOTE_TYPE_STEP_DEFINITIONS: Record<number, StepDefinition[]> = {
    [CONTRACT_TYPE_FOREST_OPERATION]: quoteForestOperationSteps,
    [CONTRACT_TYPE_SUPPLY_WOOD]: quoteSupplyWoodSteps,
}

export function getStepDefinitionsByEntityType(entityType: number): Record<number, StepDefinition[]> {
    return entityType === ENTITY_TYPE_QUOTE ? QUOTE_TYPE_STEP_DEFINITIONS : CONTRACT_TYPE_STEP_DEFINITIONS
}

export function getFilteredStepDefinitions(
    entityType: number,
    type: number,
    customFilters: Array<(step: StepDefinition) => boolean> = [],
): StepDefinition[] {
    const steps = getStepDefinitionsByEntityType(entityType)[type] ?? []
    if (!customFilters.length) {
        return steps
    }
    return steps.filter((step) => customFilters.every((filter) => filter(step)))
}

export function findFirstEnabledStep(
    steps: StepDefinition[],
    isStepValid: (step: StepDefinition) => boolean,
): StepDefinition {
    const firstDisabledIndex = steps.findIndex((step) => !isStepValid(step))
    if (firstDisabledIndex < 0) {
        return steps[steps.length - 1]!
    }
    const firstEnabledIndex = firstDisabledIndex > 0 ? firstDisabledIndex - 1 : 0
    return steps[firstEnabledIndex]!
}
